import { Injectable } from '@angular/core';

import { FixtureStage } from '@cds/betting-offer';
import { MessageType } from '@cds/push';
import { BetBuilderOfferUpdateCommand } from '@cds/push/bet-builder-commands';
import {
    FixtureUpdateCommand,
    GameDeleteCommand,
    GameUpdateCommand,
    OptionMarketDeleteCommand,
    OptionMarketUpdateCommand,
    ParticipantUpdateCommand,
} from '@cds/push/fixture-commands';
import { DetailedFixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import { BetBuilderSubscription, CdsPushService, CdsSubscription, FixtureAllSubscription } from '@frontend/sports/content-distribution/feature';
import { BaseSubscriptionService, MessageCallback, MessageHandler } from '@frontend/sports/event-subscription/feature/base-subscription';
import { updateBetbuilderPrecreatedGroup } from '@frontend/sports/event-subscription/feature/precreated-group-commands';
import { Observable, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DetailedEventSubscriptionService extends BaseSubscriptionService<EventModel> {
    constructor(
        private fixtureFactory: DetailedFixtureFactory,
        push: CdsPushService,
        protected connection: ConnectionConfig,
        private groupingService: OfferGroupingService,
    ) {
        super(push);
    }

    protected getContextSubscriptions(event: EventModel, callback: MessageCallback): CdsSubscription[] {
        const subscriptions: CdsSubscription[] = [this.getSubscription(callback, event.id, event.offerContext)];

        if (event.precreatedOptionGroups?.length) {
            const betBuilderSubscriptions = event.precreatedOptionGroups
                .filter((g) => g.builderOptionPricing && g.builderOptionPricing.groupId)
                .map((g) => new BetBuilderSubscription([g.builderOptionPricing.groupId], callback));
            subscriptions.push(...betBuilderSubscriptions);
        }

        if (event.splitFixtureIds) {
            subscriptions.push(...event.splitFixtureIds.map((split) => this.getSubscription(callback, split)));
        }
        if (event.winGameLinkedFixture) {
            subscriptions.push(this.getSubscription(callback, event.winGameLinkedFixture.id, event.winGameLinkedFixture.contexts));
        }

        return subscriptions;
    }

    private getSubscription(callback: MessageCallback, eventId: string, contexts?: string[]): FixtureAllSubscription {
        return new FixtureAllSubscription(contexts, eventId, this.connection.culture, callback);
    }

    protected getHandlers(): Map<MessageType, MessageHandler<EventModel>> {
        const handlers = new Map<MessageType, MessageHandler<EventModel>>();

        handlers.set(MessageType.GameDelete, (evt, cmd) => this.deleteOptionGroup(evt, cmd as GameDeleteCommand & OptionMarketDeleteCommand));
        handlers.set(MessageType.GameUpdate, (evt, cmd) => this.updateOptionGroup(evt, cmd as GameUpdateCommand & OptionMarketUpdateCommand));
        handlers.set(MessageType.OptionMarketDelete, (evt, cmd) => this.deleteOptionGroup(evt, cmd as GameDeleteCommand & OptionMarketDeleteCommand));
        handlers.set(MessageType.OptionMarketUpdate, (evt, cmd) => this.updateOptionGroup(evt, cmd as GameUpdateCommand & OptionMarketUpdateCommand));
        handlers.set(MessageType.FixtureUpdate, (evt, cmd) => this.updateFixture(evt, cmd as FixtureUpdateCommand));
        handlers.set(MessageType.ParticipantUpdate, (evt, cmd) => this.updateWinGameLinkedFixture(evt, cmd as ParticipantUpdateCommand));
        handlers.set(MessageType.BetBuilderOfferUpdate, (evt, cmd) => this.updateBetbuilderPrecreatedOffer(evt, cmd as BetBuilderOfferUpdateCommand));

        return handlers;
    }

    private deleteOptionGroup(event: EventModel, command: GameDeleteCommand & OptionMarketDeleteCommand): Observable<EventModel> {
        return this.groupingService.getFixtureGroupingForEventModel(event).pipe(
            map((grouping) => {
                const optionGroupId = command.gameId || command.marketId;
                this.fixtureFactory.deleteOptionGroup(event, optionGroupId, grouping);

                return event;
            }),
        );
    }

    private updateOptionGroup(event: EventModel, command: GameUpdateCommand & OptionMarketUpdateCommand): Observable<EventModel> {
        return this.groupingService.getFixtureGroupingForEventModel(event).pipe(
            map((grouping) => {
                const optionGroup = command.game || command.optionMarket;
                this.fixtureFactory.updateOptionGroup(event, optionGroup, command.fixtureId, grouping);

                return event;
            }),
        );
    }

    private updateWinGameLinkedFixture(event: EventModel, command: ParticipantUpdateCommand): EventModel {
        if (command.fixtureId === event.winGameLinkedFixture?.id) {
            const winGameLinkedFixture = { ...event.winGameLinkedFixture };
            const participantToUpdate = winGameLinkedFixture.participants.find((p) => p.id === command.participant.id);
            if (participantToUpdate) {
                participantToUpdate.options = command.participant.options ?? [];
                // eslint-disable-next-line no-param-reassign
                event.winGameLinkedFixture = winGameLinkedFixture;
            }
        }

        return event;
    }

    private updateFixture(event: EventModel, command: FixtureUpdateCommand): EventModel {
        // eslint-disable-next-line no-param-reassign
        event.live = command.stage === FixtureStage.Live;

        return event;
    }

    private updateBetbuilderPrecreatedOffer(event: EventModel, command: BetBuilderOfferUpdateCommand): EventModel {
        if (event.precreatedOptionGroups && command.sgpId) {
            updateBetbuilderPrecreatedGroup(event, command.sgpId, command.odds, command.suspensionState, command.legs);
        }

        return event;
    }
}
